import { nanoid } from "@reduxjs/toolkit";
import { api } from "../../app/api";
import isEmpty from "../../common/helpers/isEmpty";
import { EventPayload } from "../../common/types/Event";
import { Paginated } from "../../common/types/Paginated";
import { Options } from "../../common/types/requestOptions";
import {
  CustomerCompanyIdFilter,
  CustomerUser,
} from "../customer/types/queries";
import {
  CreateExpertUser,
  UpdateExpertOutOfOffice,
  UpdateExpertUser,
  UpdateExpertUserEnabledState,
} from "./types/commands";
import { CoreUser, HrExpertUser } from "./types/entities";
import {
  ExpertOutOfOfficeChanged,
  ExpertUserCreated,
  ExpertUserUpdated,
} from "./types/events";
import {
  CoreUsersQueryParameters,
  ExpertByIdQueryParameters,
} from "./types/queries";
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { User } from "./user";
import { command } from "../../app/command";

//TODO: move all amplify stuff here

export const postLogin = async (username: string, password: string) => {
  return await signIn({ username, password });
};

export const postLogout = async () => {
  return await signOut();
};

export const getCurrentAuthenticatedUser = async () => {
  return await getCurrentUser();
};

export const getAuthSession = async () => {
  return await fetchAuthSession();
};

export const getExpert = async (
  queryParameters: ExpertByIdQueryParameters,
  { requestId }: Options,
) => {
  return api
    .get<CoreUser>(`/projections/qry/expertById`, {
      requestId,
      auth: true,
      queryParams: {
        id: queryParameters.id,
      },
    })
    .then((res) => res.data);
};

export const getCoreUsers = async (
  queryParameters: CoreUsersQueryParameters,
  { requestId }: Options,
): Promise<Paginated<CoreUser[]>> => {
  const requestParams: Record<string, string> | undefined = {};

  if (queryParameters.userIds)
    requestParams.userIds = queryParameters.userIds.join(",");
  if (queryParameters.type) requestParams.type = queryParameters.type;
  if (queryParameters.enabled != null) {
    requestParams.enabled = queryParameters.enabled.toString();
  }

  return api
    .get<Paginated<CoreUser[]>>("/projections/qry/coreUsers", {
      requestId,
      auth: true,
      queryParams: !isEmpty(requestParams) ? requestParams : undefined,
    })
    .then((res) => res.data);
};

export const hrExpertById = async (id: string, { requestId }: Options) =>
  api
    .get<HrExpertUser>(`/projections/qry/hrExpertById`, {
      requestId,
      auth: true,
      queryParams: {
        id,
      },
    })
    .then((res) => res.data);

export const createExpertUser = async (params: CreateExpertUser) =>
  api
    .post<EventPayload<ExpertUserCreated>[]>("/identity/cmd/createExpert", {
      requestId: nanoid(),
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data);

export const updateExpertUser = (
  body: UpdateExpertUser,
  options: Options = {},
) =>
  command<UpdateExpertUser, ExpertUserUpdated>(
    "/identity/cmd/updateExpert",
    body,
    options,
  );

export const updateExpertUserEnabledState = async (
  params: UpdateExpertUserEnabledState,
) =>
  api
    .post<EventPayload<{}>[]>("/identity/cmd/updateExpertUserEnabledState", {
      requestId: nanoid(),
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data);

export const updateExpertOutOfOffice = (
  body: UpdateExpertOutOfOffice,
  options: Options = {},
) =>
  command<UpdateExpertOutOfOffice, ExpertOutOfOfficeChanged>(
    "/identity/cmd/updateExpertOutOfOffice",
    body,
    options,
  );

export const customerUsersByCompanyId = async (
  filters: CustomerCompanyIdFilter,
  { requestId }: Options,
) =>
  api
    .get<CustomerUser[]>(`/projections/qry/customerUsersByCompanyId`, {
      requestId,
      auth: true,
      queryParams: {
        ...filters,
      },
    })
    .then((res) => res.data);

export const constructUser = async (): Promise<User | null> => {
  const session = await getAuthSession();
  const user = await getCurrentUser();

  const attributes = session.tokens?.idToken?.payload as Record<string, string>;

  if (!attributes) return null;

  const eezyCompanyIds = attributes["custom:ecid"]?.split(",") || [];

  return {
    id: attributes["custom:uid"],
    email: attributes["email"],
    username: user.username,
    firstName: attributes["given_name"],
    lastName: attributes["family_name"],
    scope: attributes["custom:scope"],
    eezyCompanyIds,
  };
};
